import React, { useCallback, useEffect, useMemo, useState } from "react";
import TitleBar from "../../common/title-bar/title-bar";
import Button from "../../common/button/button";
import ModalView from "../../common/modal";
import TableOptionsCell from "../../common/table/table-options-cell";
import Table from "../../common/table";
import APIService from "../../Service/APIService";
import FormConfig from "../../Config/FormConfig";
import { Formik } from "formik";
import { Input, Row, Col, DatePicker, message, Upload } from "antd";
import { UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import "./CommissionStatementsCss.css";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import TableLink from "../../common/table/table-link";
import moment from "moment";
import { colors } from "../../theme/colors";
import LazyEntitySelect from "../../common/multi-select-input/GenericAsyncSelect";
import Text from "../../common/typography/text";
import DatePickerComponent from "../../common/date-picker/index.js";
import TextInput from "../../common/text-input/index.js";
import dayjs from "dayjs";
import Papa from "papaparse";
import { useAuth } from "../AuthProvider.js";
import { hasPermission } from "../../Config/permissions.js";
import { canAccessCommissionsPage } from "./commissionPageAccess.js";
// import { Spin, Flex } from "@tremor/react";
// import { LoadingOutlined } from "@ant-design/icons";

const CommissionStatements = () => {
  const { user } = useAuth();
  const userData = user;
  //   const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [commissionStatements, setCommissionStatements] = useState([]);
  const [initialValues, setInitialValues] = useState(null);
  const apiPath = "/GetInvoiceFilterOptions";
  const [appliedDate, setAppliedDate] = React.useState(null); // apply comm in full
  const [statementMonthComm, setStatementMonthComm] = useState(null); // apply comm and apply comm in full

  const handleModalClick = () => {
    setIsModalOpen(!isModalOpen);
  };

  const [bulkApplyModalOpen, setBulkApplyModalOpen] = useState(false);
  const [csvUploadList, setCsvUploadList] = useState([]);
  const [isParsingCsv, setIsParsingCsv] = useState(false);
  const [invoiceOptionsCache, setInvoiceOptionsCache] = useState([]);
  const [invoiceCacheLoading, setInvoiceCacheLoading] = useState(false);
  const [invoiceCacheError, setInvoiceCacheError] = useState(null);

  const fetchInvoiceOptionsCache = async () => {
    const apiServe = new APIService();
    const pageSize = 500;
    let cursor = null;
    const dedupMap = new Map();

    do {
      const params = new URLSearchParams({ type: "invoices", limit: String(pageSize) });
      if (cursor) params.set("cursor", cursor);

      const response = await apiServe.getDataFromBackend(
        `${apiPath}?${params.toString()}`
      );

      const data = response?.data ?? response ?? {};
      const items = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
          ? data
          : [];

      items
        .map(mapInvoiceOption)
        .filter((opt) => opt?.value != null && opt?.label != null)
        .forEach((opt) => {
          const key =
            buildInvoiceKey(
              opt.InvoiceNumber ?? opt.invoiceNumber,
              opt.ManufacturerID ?? opt.manufacturerId
            ) || String(opt.value ?? opt.ID ?? opt.id);
          if (!dedupMap.has(key)) dedupMap.set(key, opt);
        });

      cursor = data?.nextCursor ?? null;
    } while (cursor);

    return Array.from(dedupMap.values());
  };

  const ensureInvoiceCache = async () => {
    if (invoiceCacheLoading || invoiceOptionsCache.length) return;
    setInvoiceCacheError(null);
    setInvoiceCacheLoading(true);
    try {
      const cache = await fetchInvoiceOptionsCache();
      setInvoiceOptionsCache(cache);
    } catch (error) {
      console.error("Failed to load invoice options", error);
      setInvoiceCacheError("Unable to load invoice list for CSV validation.");
      message.error("Unable to load invoice list for CSV validation. Please retry.");
    } finally {
      setInvoiceCacheLoading(false);
    }
  };

  const handleBulkApplyOpen = async () => {
    setCsvUploadList([]);
    setIsParsingCsv(false);
    setBulkApplyModalOpen(true);
    await Promise.all([getDateAppliedLimits(), ensureInvoiceCache()]);
  };

  const handleBulkApplyClose = () => {
    setBulkApplyModalOpen(false);
    setCsvUploadList([]);
    setIsParsingCsv(false);
  };

  const tableHeadersCommissionsStatements = [
    {
      headerName: "Month",
      field: "StatementMonthYear",
      filter: true,
      cellRenderer: (params) => {
        return (
          <TableLink
            value={params.data.StatementMonthYear}
            path={`/CommissionStatementsDetails/${params.data.ID}`}
          />
        );
      },
    },
    {
      headerName: "Statement Start Date",
      field: "StatementStartDate",
      filter: true,
      cellRenderer: (params) => {
        // const value = params.data.StatementStartDate.split("T")[0];
        // console.log(
        //   moment(params.data.StatementStartDate).format("MM-DD-YYYY"),
        //   "value >>>>> 1"
        // );
        return `${moment(params.data.StatementStartDate).format("MM-DD-YYYY")}`;
      },
    },
    {
      headerName: "Statement End Date",
      field: "StatementEndDate",
      filter: true,
      cellRenderer: (params) => {
        // const value = params.data.StatementEndDate.split("T")[0];
        return `${moment(params.data.StatementEndDate).format("MM-DD-YYYY")}`;
      },
    },
    {
      headerName: "Distributors",
      field: "DistributorCount",
      filter: true,
    },
    {
      headerName: "Total Comm. Applied",
      field: "TotalCommissionApplied",
      filter: true,
      cellDataType: "number",
      valueGetter: (params) =>
        parseFloat(
          String(params?.data?.TotalCommissionApplied).replace(/[^0-9.-]+/g, "")
        ),
      cellRenderer: (params) => {
        const value = parseFloat(
          String(params?.data?.TotalCommissionApplied).replace(/[^0-9.-]+/g, "")
        );
        return `$${value.toLocaleString()}`;
      },
    },
    // {
    //   headerName: "North Atlantic Total Comm. Applied",
    //   field: "TotalCommissionAppliedNorthAtlantic",
    //   filter: true,
    //   cellDataType: "number",
    //   valueGetter: (params) =>
    //     parseFloat(
    //       String(params?.data?.TotalCommissionAppliedNorthAtlantic).replace(/[^0-9.-]+/g, "")
    //     ),
    //   cellRenderer: (params) => {
    //     const value = parseFloat(
    //       String(params?.data?.TotalCommissionAppliedNorthAtlantic).replace(/[^0-9.-]+/g, "")
    //     );
    //     return `$${value.toLocaleString()}`;
    //   },
    // },
    // {
    //   headerName: "North Central Total Comm. Applied",
    //   field: "TotalCommissionAppliedNorthCentral",
    //   filter: true,
    //   cellDataType: "number",
    //   valueGetter: (params) =>
    //     parseFloat(
    //       String(params?.data?.TotalCommissionAppliedNorthCentral).replace(/[^0-9.-]+/g, "")
    //     ),
    //   cellRenderer: (params) => {
    //     const value = parseFloat(
    //       String(params?.data?.TotalCommissionAppliedNorthCentral).replace(/[^0-9.-]+/g, "")
    //     );
    //     return `$${value.toLocaleString()}`;
    //   },
    // },
    // {
    //   headerName: "Pacific West Total Comm. Applied",
    //   field: "TotalCommissionAppliedPacificWest",
    //   filter: true,
    //   cellDataType: "number",
    //   valueGetter: (params) =>
    //     parseFloat(
    //       String(params?.data?.TotalCommissionAppliedPacificWest).replace(/[^0-9.-]+/g, "")
    //     ),
    //   cellRenderer: (params) => {
    //     const value = parseFloat(
    //       String(params?.data?.TotalCommissionAppliedPacificWest).replace(/[^0-9.-]+/g, "")
    //     );
    //     return `$${value.toLocaleString()}`;
    //   },
    // },
    // {
    //   headerName: "South Atlantic Total Comm. Applied ",
    //   field: "TotalCommissionAppliedSouthAtlantic",
    //   filter: true,
    //   cellDataType: "number",
    //   valueGetter: (params) =>
    //     parseFloat(
    //       String(params?.data?.TotalCommissionAppliedSouthAtlantic).replace(/[^0-9.-]+/g, "")
    //     ),
    //   cellRenderer: (params) => {
    //     const value = parseFloat(
    //       String(params?.data?.TotalCommissionAppliedSouthAtlantic).replace(/[^0-9.-]+/g, "")
    //     );
    //     return `$${value.toLocaleString()}`;
    //   },
    // },
    // {
    //   headerName: "South Central Total Comm. Applied ",
    //   field: "TotalCommissionAppliedSouthCentral",
    //   filter: true,
    //   cellDataType: "number",
    //   valueGetter: (params) =>
    //     parseFloat(
    //       String(params?.data?.TotalCommissionAppliedSouthCentral).replace(/[^0-9.-]+/g, "")
    //     ),
    //   cellRenderer: (params) => {
    //     const value = parseFloat(
    //       String(params?.data?.TotalCommissionAppliedSouthCentral).replace(/[^0-9.-]+/g, "")
    //     );
    //     return `$${value.toLocaleString()}`;
    //   },
    // },
    // {
    //   headerName: "Total Comm. Recd.",
    //   field: "TotalCommissionReceived",
    //   filter: true,
    //   cellDataType: "number",
    //   valueGetter: (params) =>
    //     parseFloat(
    //       String(params?.data?.TotalCommissionReceived).replace(
    //         /[^0-9.-]+/g,
    //         ""
    //       )
    //     ),
    //   cellRenderer: (params) => {
    //     const value = parseFloat(
    //       String(params?.data?.TotalCommissionReceived).replace(
    //         /[^0-9.-]+/g,
    //         ""
    //       )
    //     );
    //     return `$${value.toLocaleString()}`;
    //   },
    // },
    // {
    //   headerName: "Total Comm. Due",
    //   field: "TotalCommissionDue",
    //   filter: true,
    //   cellDataType: "number",
    //   valueGetter: (params) =>
    //     parseFloat(
    //       String(params?.data?.TotalCommissionDue).replace(/[^0-9.-]+/g, "")
    //     ),
    //   cellRenderer: (params) => {
    //     const value = parseFloat(
    //       String(params?.data?.TotalCommissionDue).replace(/[^0-9.-]+/g, "")
    //     );
    //     return `$${value.toLocaleString()}`;
    //   },
    // },
    {
      headerName: "Options",
      field: "",
      sortable: false,
      cellRenderer: (params) => (
        <TableOptionsCell
          optionOne={
            params.data.IsGenerated
              ? "View"
              : hasPermission(userData, "commission_stmt.edit")
              ? "Generate Statements"
              : null
          }
          optionOneType={params.data.IsGenerated ? "warning" : "primary"}
          optionOneClickFn={
            params.data.IsGenerated
              ? () =>
                (window.location.href = `/CommissionStatementsDetails/${params.data.ID}`)
              : hasPermission(userData, "commission_stmt.edit")
              ? () => generateStatements(params.data)
              : undefined
            }
          // optionTwo={!params.data.IsGenerated ? "View" : ""}
          // optionTwoType={!params.data.IsGenerated ? "warning" : "primary"}
          // optionTwoClickFn={
          //   !params.data.IsGenerated
          //     ? () =>
          //         (window.location.href = `/CommissionStatementsDetails/${params.data.ID}`)
          //     : () => console.log("No action for option two")
          // }
          rowData={params.data}
        />
      ),
    },
  ];

  const generateStatements =  async(data) => {
    console.log("generateStatements", data);
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: colors["successColor"],
      cancelButtonColor: colors["failureColor"],
      confirmButtonText: "Yes, generate it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        const apiServe = new APIService();
        const response = await apiServe.addDataToBackend(
          data,
          "GenerateCommissionStatement"
        );
        console.log("Response from API:", response);
        if (response?.status === 200 || response?.status === 201) {
          const updatedData = await getData();
          setCommissionStatements(updatedData);
          setLoading(false);
          Swal.fire(
            "Success!",
            "Commission Statement generated successfully.",
            "success"
          );
        } else {
          setLoading(false);
          Swal.fire(
            "Error",
            "Something went wrong while generating the commission statement.",
            "error"
          );
        }
      }
    });
  };

  const getData = async () => {
    const apiServe = new APIService();
    const response = await apiServe.getDataFromBackend(
      "getcommissionstatements"
    );
    console.log("getData", response.data.data);
    return response?.data?.data;
  };

  const generateInitialValues = (fieldVal, data = null) => {
    let obj = {};
    if (!data) {
      for (const cur of fieldVal) {
        obj[cur.backendName] = null;
      }
    } else {
      for (const cur of fieldVal) {
        obj[cur.backendName] =
          cur.backendName in data ? data[cur.backendName] : null;
      }
    }
    return obj;
  };

  const generateFieldsArray = (data = null) => {
    const fields = FormConfig.NewCommissionStatement;
    // setFieldsArr(invoiceFields);
    const val = generateInitialValues(fields);
    setInitialValues(val);
    return val;
  };

  useEffect(() => {
    setLoading(true);
    console.log("CommissionStatements.js");
    if (userData && !canAccessCommissionsPage(userData)) {
      if (userData.Type === "Account") {
        navigate(`/Account/${userData.AccountID}`);
      } else if (userData.Type === "Distributor") {
        navigate(`/Distributor/${userData.DistributorID}`);
      } else {
        navigate("/");
      }
      return;
    }
    getData().then((data) => {
      if (data) {
        setCommissionStatements(data);
      }
      setLoading(false);
    });
    const val = generateFieldsArray();
    setInitialValues(val);
  }, []);

  const submitHandler = async (values) => {
    console.log("Form submitted with values:", values);

    // date validation
    if (
      values.StatementStartDate &&
      values.StatementEndDate &&
      moment(values.StatementEndDate).isBefore(moment(values.StatementStartDate))
    ) {
      message.error("Statement End Date cannot be before Statement Start Date.");
      return;
    }

    // date overlap validation
    const overlap = commissionStatements.some((statement) => {
      if (
        !statement.StatementStartDate ||
        !statement.StatementEndDate ||
        !values.StatementStartDate ||
        !values.StatementEndDate
      ) {
        return false;
      }
      const existingStart = moment(statement.StatementStartDate);
      const existingEnd = moment(statement.StatementEndDate);
      console.log("newStart", values.StatementStartDate);
      const newStart = moment(new Date(values.StatementStartDate));
      console.log("newStart", newStart);
      const newEnd = moment(new Date(values.StatementEndDate));

      return (
        (newStart.isBetween(existingStart, existingEnd, null, '[]')) ||
        (newEnd.isBetween(existingStart, existingEnd, null, '[]')) ||
        (existingStart.isBetween(newStart, newEnd, null, '[]')) ||
        (existingEnd.isBetween(newStart, newEnd, null, '[]'))
      );
    });

    if (overlap) {
      message.error("The new statement period overlaps with an existing statement.");
      return;
    }

    console.log("No overlaps detected. Proceeding with submission.");

    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: colors["successColor"],
      cancelButtonColor: colors["failureColor"],
      confirmButtonText: "Yes, create it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const apiServe = new APIService();
        const response = await apiServe.addDataToBackend(
          values,
          "createcommissionstatement"
        );
        console.log("Response from API:", response);
        // if (response?.status === 200 || response?.status === 201) {
        //   Swal.fire(
        //     'Submitted!',
        //     'Order has been submitted.',
        //     'success'
        //   ).then(() => {
        //     window.location.reload();
        //   });
        // }
        if (response?.status === 200 || response?.status === 201) {
          Swal.fire(
            "Success!",
            "Commission Statement create successfully.",
            "success"
          );
          setIsModalOpen(false);
          const updatedData = await getData();
          setCommissionStatements(updatedData);
        } else {
          Swal.fire(
            "Error",
            "Something went wrong while creating the commission statement.",
            "error"
          );
        }
      }
    });
  };

  const buildInvoiceKey = useCallback((invoiceNumber, manufacturerId) => {
    const normalizedNumber = String(invoiceNumber ?? "").trim().toUpperCase();
    const normalizedManufacturerIdRaw =
      manufacturerId === null || manufacturerId === undefined
        ? ""
        : String(manufacturerId).trim();
    const normalizedManufacturerId = Number(normalizedManufacturerIdRaw);
    if (!normalizedNumber || !normalizedManufacturerIdRaw) return "";
    if (!Number.isFinite(normalizedManufacturerId)) return "";
    return `${normalizedNumber}|${normalizedManufacturerId}`;
  }, []);

  const [invoices, setInvoices] = useState([]);
  const mapInvoiceOption = (item) => {
    const invoiceNumber = String(item.InvoiceNumber ?? item.invoiceNumber ?? "").trim();
    const optionKeyRaw = item.Key ?? item.key ?? item.Value ?? item.value ?? "";
    const manufacturerIdRaw =
      item.ManufacturerID ??
      item.manufacturerId ??
      item.ManufacturerId ??
      item.manufacturerID;
    const manufacturerIdNormalized =
      manufacturerIdRaw === null ||
      manufacturerIdRaw === undefined ||
      String(manufacturerIdRaw).trim() === ""
        ? null
        : Number(String(manufacturerIdRaw).trim());
    const manufacturerId =
      manufacturerIdNormalized !== null && Number.isFinite(manufacturerIdNormalized)
        ? manufacturerIdNormalized
        : null;
    const manufacturerName =
      item.ManufacturerName ??
      item.manufacturerName ??
      item.AccountName ??
      item.accountName ??
      "";
    const derivedKey = buildInvoiceKey(invoiceNumber, manufacturerId);
    const key =
      (typeof optionKeyRaw === "string" && optionKeyRaw.includes("|") && optionKeyRaw) ||
      derivedKey;
    const invoiceId = item.ID ?? item.id ?? item.InvoiceID ?? item.invoiceId;
    const value =
      key ||
      (typeof optionKeyRaw === "string" && optionKeyRaw) ||
      (invoiceNumber && manufacturerId != null
        ? `${invoiceNumber}|${manufacturerId}`
        : String(invoiceId ?? invoiceNumber));

    return {
      ...item,
      value,
      label: [manufacturerName, invoiceNumber].filter(Boolean).join(" - "),
      InvoiceNumber: invoiceNumber,
      ManufacturerID: manufacturerId,
      ManufacturerName: manufacturerName,
      InvoiceID: invoiceId,
      ID: invoiceId ?? item.ID ?? item.id,
      Key: key,
      Value: value,
    };
  };

  const parseInvoiceCsv = (file) =>
    new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          if (results?.errors?.length) {
            reject(
              new Error(
                "Unable to parse CSV. Please check the file formatting."
              )
            );
            return;
          }
          resolve(results?.data || []);
        },
        error: (error) => reject(error),
      });
    });

  const invoiceOptionsByKey = useMemo(() => {
    const index = new Map();
    invoiceOptionsCache.forEach((opt) => {
      const computedKey = buildInvoiceKey(
        opt.InvoiceNumber ?? opt.invoiceNumber,
        opt.ManufacturerID ?? opt.manufacturerId
      );
      const keyFromOption =
        (typeof opt.Key === "string" && opt.Key.includes("|") && opt.Key) ||
        (typeof opt.Value === "string" && opt.Value.includes("|") && opt.Value) ||
        computedKey ||
        null;
      if (!keyFromOption) return;
      if (!index.has(keyFromOption)) index.set(keyFromOption, opt);
    });
    return index;
  }, [invoiceOptionsCache, buildInvoiceKey]);

  const handleCsvUpload = async ({ file, onSuccess, onError }) => {
    try {
      const isCsv = file?.name?.toLowerCase().endsWith(".csv");
      if (!isCsv) {
        message.error("Please upload a CSV file.");
        onError?.(new Error("Invalid file type"));
        return;
      }
      const maxSizeBytes = 2 * 1024 * 1024; // 2MB guard
      if (file?.size && file.size > maxSizeBytes) {
        message.error("CSV is too large. Please upload a file under 2MB.");
        onError?.(new Error("File too large"));
        return;
      }

      if (invoiceCacheLoading) {
        onError?.(new Error("Invoice list is still loading."));
        message.info("Invoice list is loading. Please wait a moment and try again.");
        return;
      }
      if (!invoiceOptionsCache.length || invoiceCacheError) {
        onError?.(new Error("Invoice list not available."));
        message.error("Invoice list not available. Please close and reopen the modal to reload.");
        return;
      }

      setIsParsingCsv(true);
      const rows = await parseInvoiceCsv(file);

      const parsedRows = rows
        .map((row) => {
          const invoiceNumber = String(
            row?.InvoiceNumber ??
            row?.invoicenumber ??
            row?.invoiceNumber ??
            row?.InvoiceNo ??
            row?.invoiceNo ??
            row?.["Invoice Number"] ??
            row?.["invoice number"] ??
            ""
          ).trim();
          const manufacturerIdRaw =
            row?.ManufacturerID ??
            row?.manufacturerid ??
            row?.manufacturerId ??
            row?.ManufacturerId ??
            row?.["Manufacturer ID"] ??
            row?.["manufacturer id"] ??
            "";
          const manufacturerId = Number(manufacturerIdRaw);
          return { invoiceNumber, manufacturerId };
        })
        .filter(
          ({ invoiceNumber, manufacturerId }) =>
            invoiceNumber && Number.isFinite(manufacturerId)
        );

      if (!parsedRows.length) {
        throw new Error("No InvoiceNumber and ManufacturerID rows found in the CSV.");
      }

      const invoiceEntries = new Map();
      parsedRows.forEach(({ invoiceNumber, manufacturerId }) => {
        const key = buildInvoiceKey(invoiceNumber, manufacturerId);
        if (!key) return;
        if (!invoiceEntries.has(key)) {
          invoiceEntries.set(key, { invoiceNumber, manufacturerId });
        }
      });

      if (!invoiceEntries.size) {
        throw new Error("No valid InvoiceNumber and ManufacturerID combinations found in the CSV.");
      }

      const lookupResults = Array.from(invoiceEntries.values()).map(
        ({ invoiceNumber, manufacturerId }) => {
          const key = buildInvoiceKey(invoiceNumber, manufacturerId);
          const matched =
            key
              ? invoiceOptionsByKey.get(key) ||
              invoiceOptionsByKey.get(String(key).toUpperCase()) ||
              null
              : null;
          return { key, invoiceNumber, manufacturerId, option: matched };
        }
      );

      const foundOptions = lookupResults
        .filter((res) => res.option)
        .map((res) => res.option);
      const missingInvoices = lookupResults
        .filter((res) => !res.option)
        .map((res) => `${res.invoiceNumber}${res.manufacturerId ? ` (Manufacturer ${res.manufacturerId})` : ""}`);

      if (!foundOptions.length) {
        throw new Error("No invoices from CSV matched existing invoices.");
      }

      const existingKeys = new Set(
        invoices
          .map((inv) =>
            buildInvoiceKey(
              inv.InvoiceNumber ?? inv.invoiceNumber,
              inv.ManufacturerID ?? inv.manufacturerId ?? inv.manufacturerID
            )
          )
          .filter(Boolean)
      );
      const addedKeys = new Set();
      const mergedInvoices = [...invoices];

      foundOptions.forEach((inv) => {
        const invKey = buildInvoiceKey(
          inv.InvoiceNumber ?? inv.invoiceNumber,
          inv.ManufacturerID ?? inv.manufacturerId
        );
        if (!invKey || existingKeys.has(invKey) || addedKeys.has(invKey)) return;
        addedKeys.add(invKey);
        mergedInvoices.push(inv);
      });

      setInvoices(mergedInvoices);
      setCsvUploadList([{ name: file.name, status: "done", uid: file.uid || file.name }]);
      onSuccess?.("ok");

      if (missingInvoices.length) {
        const preview = missingInvoices.filter(Boolean).slice(0, 3).join(", ");
        message.warning(
          `Some invoices were not found: ${preview}${missingInvoices.length > 3 ? "..." : ""}`
        );
      }
      message.success(
        `Added ${foundOptions.length} invoice${foundOptions.length > 1 ? "s" : ""} from CSV.`
      );
    } catch (error) {
      console.error("CSV upload failed", error);
      const errMsg =
        error?.message || "Failed to process CSV. Please verify the columns.";
      message.error(errMsg);
      setCsvUploadList([]);
      onError?.(error);
    } finally {
      setIsParsingCsv(false);
    }
  };

  const csvUploadProps = {
    name: "file",
    multiple: false,
    accept: ".csv",
    showUploadList: false,
    maxCount: 1,
    customRequest: handleCsvUpload,
    fileList: csvUploadList,
    onChange: ({ fileList }) => setCsvUploadList(fileList),
  };

  const downloadCsvTemplate = () => {
    const template = "InvoiceNumber,ManufacturerID\n";
    const blob = new Blob([template], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "bulk-apply-commissions-template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const applyBulkCommissions = async () => {
    console.log("applyBulkCommissions", invoices);
    if (invoices.length === 0) {
      message.error("Please select at least one invoice.");
      return;
    }
    if (!appliedDate) {
      message.error("Please select Date Applied.");
      return;
    }
    setLoading(true);
    const apiServe = new APIService();
    const invoicePayload = invoices.map((inv) => {
      const manufacturerIdRaw =
        inv.ManufacturerID ?? inv.manufacturerId ?? inv.manufacturerID;
      const manufacturerId = Number(manufacturerIdRaw);
      return {
        InvoiceNumber: inv.InvoiceNumber ?? inv.invoiceNumber,
        ManufacturerID: Number.isFinite(manufacturerId) ? manufacturerId : manufacturerIdRaw,
        ManufacturerName: inv.ManufacturerName ?? inv.manufacturerName,
        Name: inv.Name ?? inv.label ?? inv.name,
        InvoiceID: inv.InvoiceID ?? inv.ID ?? inv.id,
        ID: inv.ID ?? inv.InvoiceID ?? inv.id,
        value: inv.value,
      };
    });
    const response = await apiServe.addDataToBackend({
      Invoices: invoicePayload,
      DateApplied: appliedDate,
      StatementMonth: statementMonthComm
    },
      "BulkApplyCommissions"
    );
    console.log("Response from API:", response);
    if (response?.status === 202) {
      setLoading(false);
      Swal.fire(
        "Success!",
        "Bulk Apply Commissions request accepted successfully.",
        "success"
      );
      setBulkApplyModalOpen(false);
      const updatedData = await getData();
      setCommissionStatements(updatedData);
    } else {
      const errorMsg = response?.data?.error || "Something went wrong while applying commissions.";
      setLoading(false);
      Swal.fire(
        "Error",
        errorMsg,
        "error"
      );
    }
  }

  const getStatementData = async (dateApp) => {
    const apiServe = new APIService();
    const response = await apiServe.getDataFromBackend(
      `GetStatementDataForDateApplied?DateApplied=${dateApp}`
    );

    return response?.data?.data;
  };

  const [maxDateApplied, setMaxDateApplied] = useState(null);
  const [minDateApplied, setMinDateApplied] = useState(null);

  const getDateAppliedLimits = async () => {
    // return;
    const apiServe = new APIService();
    const response = await apiServe.getDataFromBackend("GetDateAppliedLimits");
    console.log("getStatementData", response);
    const maxDate = dayjs(response?.data?.data?.MaxEndDate);
    const minDate = dayjs(response?.data?.data?.MinStartDate);
    console.log("maxDate", maxDate);
    setMaxDateApplied(maxDate);
    setMinDateApplied(minDate);
    return { maxDate, minDate };
  };

  const handleAppliedDateChange = async (date) => {
    setAppliedDate(date);

    const statementData = await getStatementData(date);

    if (statementData) {
      setStatementMonthComm(statementData?.StatementMonthYear);
    } else {
      setStatementMonthComm("NA");
    }
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
      {/* {loading &&
        <Flex align="center" gap="middle">
          <Spin
            indicator={
              <LoadingOutlined
                style={{
                  fontSize: 48,
                }}
                spin
              />
            }
            fullscreen
          />
        </Flex>
      } */}
      <TitleBar
        entityName="Commission"
        label="Commission Statements"
        rightFilterBarComponents={
          <>
            {hasPermission(userData, "commission_stmt.edit") &&
              <Button variant="primary" onClick={handleModalClick}>
                New Statement Month
              </Button>
            }
            {userData?.Type === "Admin" && userData?.IsSuperAdmin === true &&
              <Button variant="primary" onClick={() => handleBulkApplyOpen()}>
                Bulk Apply Commissions
              </Button>
            }
          </>
        }
      />
      <Table
        rowData={commissionStatements}
        columnDefs={tableHeadersCommissionsStatements}
        loading={loading}
      />
      {isModalOpen && (
        <ModalView
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          modalTitle="Add New Statement Month"
          textInputComponent={
            initialValues && (
              <div>
                <Formik
                  initialValues={initialValues}
                  onSubmit={submitHandler}
                  enableReinitialize
                >
                  {(formik) => (
                    <form onSubmit={formik.handleSubmit}>
                      <Row gutter={16} className="form-row">
                        <Col span={12} style={{ width: "100%" }}>
                          <label className="form-label">
                            Statement Start Date
                          </label>
                          <DatePicker
                            format="MM-DD-YYYY"
                            onBlur={() =>
                              formik.setFieldTouched("StatementStartDate")
                            }
                            onChange={(date, dateString) => {
                              formik.setFieldValue("StatementStartDate", date);
                            }}
                            title={formik.values["StatementStartDate"]}
                            value={formik.values["StatementStartDate"]}
                            style={{ width: "100%" }}
                          />
                        </Col>
                        <Col span={12} style={{ width: "100%" }}>
                          <label className="form-label">
                            Statement End Date
                          </label>
                          <DatePicker
                            format="MM-DD-YYYY"
                            onBlur={() =>
                              formik.setFieldTouched("StatementEndDate")
                            }
                            onChange={(date, dateString) => {
                              formik.setFieldValue("StatementEndDate", date);
                            }}
                            title={formik.values["StatementEndDate"]}
                            value={formik.values["StatementEndDate"]}
                            style={{ width: "100%" }}
                          />
                        </Col>
                        <Col span={12} style={{ width: "100%" }}>
                          <label className="form-label">Payout Date</label>
                          <DatePicker
                            format="MM-DD-YYYY"
                            onBlur={() => formik.setFieldTouched("PayoutDate")}
                            onChange={(date, dateString) => {
                              formik.setFieldValue("PayoutDate", date);
                            }}
                            title={formik.values["PayoutDate"]}
                            value={formik.values["PayoutDate"]}
                            style={{ width: "100%" }}
                          />
                        </Col>
                        <Col span={12} style={{ width: "100%" }}>
                          <label className="form-label">
                            Statement Month & Year
                          </label>
                          <Input
                            id="StatementMonthYear"
                            name="StatementMonthYear"
                            value={formik.values.StatementMonthYear}
                            // disabled
                            onChange={formik.handleChange}
                          />
                        </Col>
                      </Row>
                      <Row gutter={16} className="form-row">
                        {userData?.Type === "Admin" && (
                          <Button
                            variant="table"
                            subVariant="success"
                            style={{ marginTop: "16px" }}
                          >
                            Save
                          </Button>
                        )}
                      </Row>
                    </form>
                  )}
                </Formik>
              </div>
            )
          }
        />
      )}

      {bulkApplyModalOpen && (
        <ModalView
          isOpen={bulkApplyModalOpen}
          onClose={handleBulkApplyClose}
          modalTitle="Bulk Apply Commissions"
          textInputComponent={
            <>
              <div
                className="sales-report-modal-field"
                style={{ alignSelf: "stretch" }}
              >
                <div className="text-label">
                  <Text variant="bodyText">Upload CSV</Text>
                </div>
                <div className="text-input" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <Upload
                    {...csvUploadProps}
                    disabled={isParsingCsv || loading}
                  >
                    <Button
                      variant="table"
                      subVariant="primary"
                      disabled={isParsingCsv || loading}
                      icon={<UploadOutlined />}
                      style={{ width: "100%" }}
                    >
                      {isParsingCsv ? "Processing..." : "Upload CSV"}
                    </Button>
                  </Upload>
                  <DownloadOutlined
                    onClick={() => {
                      if (loading) return;
                      downloadCsvTemplate();
                    }}
                    style={{ fontSize: 20, cursor: loading ? "not-allowed" : "pointer", color: colors.primaryColor }}
                    className={loading ? "disabled" : ""}
                    aria-label="Download CSV template"
                  />
                </div>
              </div>
              <div
                className="sales-report-modal-field"
                style={{ alignSelf: "stretch" }}
              >
                <div className="text-label">
                  <Text variant="bodyText">Invoices</Text>
                </div>
                <div className="text-input">
                  <LazyEntitySelect
                    value={invoices}
                    onChange={setInvoices}
                    placeholder="Search invoices..."
                    apiPath={apiPath}
                    mapItem={mapInvoiceOption}
                    getOptionValueFn={(opt) =>
                      opt.Key ||
                      opt.Value ||
                      buildInvoiceKey(opt.InvoiceNumber ?? opt.invoiceNumber, opt.ManufacturerID ?? opt.manufacturerId) ||
                      String(opt.value)
                    }
                    staticParams={{ type: "invoices" }}
                  />
                </div>
              </div>
              <div className="field-group-wrapper">
                <div className="field-group">
                  <DatePickerComponent
                    format="MM-DD-YYYY"
                    titleText="Date Applied"
                    isRequired
                    disabledDate={(currentDate) => {
                      // Disable if before minDateApplied or after maxDateApplied
                      if (!minDateApplied || !maxDateApplied) return true;
                      return (
                        currentDate.isBefore(minDateApplied.startOf("day")) ||
                        currentDate.isAfter(maxDateApplied.endOf("day"))
                      );
                    }}
                    value={appliedDate}
                    onChange={handleAppliedDateChange}
                  />
                  <TextInput
                    titleText="Statement Month"
                    placeholder="Statement Month"
                    isRequired
                    value={statementMonthComm}
                    setValue={setStatementMonthComm}
                    isDisabled={true}
                  />
                </div>
              </div>
            </>
          }
          footerButtonWrapper={
            <div className="footer-button-wrapper">
              <Button variant="table" subVariant="primary" onClick={applyBulkCommissions}>
                Apply Commissions
              </Button>
            </div>
          }
        />
      )}
    </div>
  );
};

export default CommissionStatements;
